import ApiService from '@/lib/ApiService'
import { encodeBase64 } from '@/utils/crypto'
import type {
    SignInCredential,
    SignInResponse,
    SignUpCredential,
    ForgotPassword,
    ResetPassword,
} from '@/types/auth'

import { isAxiosError } from 'axios'
import type { AxiosResponse } from 'axios'
import { logger } from '@/utils/logger'

export interface BackendError {
    code: string
    detail: string
    status: number
    title: string
    type: string
    traceId?: string
}

export async function apiSignIn(
    credential: SignInCredential,
): Promise<AxiosResponse<string>> {
    try {
        logger.debug('Starting sign-in process:', {
            userName: credential.userName,
            languageId: credential.languageId,
            hasPassword: !!credential.password
        })

        // Encode the credentials using Base64 (this is obfuscation, not encryption - relies on HTTPS for transport security)
        const encodedData = encodeBase64({
            Email: credential.userName,
            Password: credential.password,
            LanguageId: credential.languageId,
        })

        const requestData = {
            data: encodedData,
        }

        logger.debug('Sending base64-encoded sign-in request')
        const response = await ApiService.fetchData<string>({
            url: '/api/auth/login',
            method: 'post',
            data: requestData,
        })

        // If backend returns error (e.g. 401 or 429) `request` resolves with an AxiosError
        // In that case propagate an exception so calling layers can handle uniformly in a `catch`.
        if (response?.status !== 200) {
            const axiosErr = isAxiosError(response) ? response : null
            const backendStatus: number | undefined =
                axiosErr?.response?.status ?? response.status
            const responseData: unknown = axiosErr
                ? axiosErr.response?.data
                : (response as AxiosResponse<string>).data
            let backendMessage = ''
            if (responseData && typeof responseData === 'object' && 'detail' in responseData) {
                backendMessage = (responseData as BackendError).detail
            } else if (typeof responseData === 'string') {
                backendMessage = responseData
            }

            logger.error('Sign-in failed with non-200 response:', {
                status: backendStatus,
                message: backendMessage,
                responseData,
                userName: credential.userName
            })

            const errorToThrow = new Error(backendMessage) as Error & {
                statusCode?: number
                response?: unknown
                detail?: string
            }
            errorToThrow.statusCode = backendStatus
            errorToThrow.response = axiosErr?.response ?? response
            errorToThrow.detail = backendMessage
            throw errorToThrow
        }

        logger.debug('Sign-in successful:', {
            status: response.status,
            hasResponseData: !!(response as AxiosResponse<string>).data,
            userName: credential.userName
        })
        return response as AxiosResponse<string>
    } catch (error: unknown) {
        if (error && typeof error === 'object' && 'response' in error) {
            type ErrorResponse = {
                response?: {
                    data?: BackendError | string
                    status?: number
                }
                message: string
            }

            const axiosError = error as ErrorResponse

            const errorData = axiosError.response?.data
            const statusCode = axiosError.response?.status

            // Handle rate limiting (429) specifically
            if (statusCode === 429) {
                let rateLimitMessage =
                    'Too many login attempts. Please try again later.'

                if (typeof errorData === 'string') {
                    rateLimitMessage = errorData
                } else if (
                    errorData &&
                    typeof errorData === 'object' &&
                    'detail' in errorData
                ) {
                    rateLimitMessage =
                        (errorData as BackendError).detail || rateLimitMessage
                }

                logger.error('Rate limit exceeded during sign-in:', {
                    statusCode,
                    message: rateLimitMessage,
                    userName: credential.userName,
                    errorData
                })

                const errorToThrow = new Error(rateLimitMessage) as Error & {
                    statusCode: number
                    code: string
                    type: string
                    title: string
                    detail: string
                    response: unknown
                }

                errorToThrow.statusCode = 429
                errorToThrow.code = 'TOO_MANY_REQUESTS'
                errorToThrow.type =
                    'https://tools.ietf.org/html/rfc6585#section-4'
                errorToThrow.title = 'Too Many Requests'
                errorToThrow.detail = rateLimitMessage
                errorToThrow.response = error

                throw errorToThrow
            }

            // Handle other errors
            let errorMessage = 'Unable to connect. Try again later'
            let errorCode: string | undefined
            let errorType: string | undefined
            let errorTitle: string | undefined
            let errorTraceId: string | undefined

            if (typeof errorData === 'string') {
                errorMessage = errorData
            } else if (errorData && typeof errorData === 'object') {
                const backendError = errorData as Partial<BackendError>
                errorMessage = backendError.detail || errorMessage
                errorCode = backendError.code
                errorType = backendError.type
                errorTitle = backendError.title
                errorTraceId = backendError.traceId
            }

            logger.error('Sign-in error with response:', {
                statusCode,
                errorMessage,
                errorCode,
                errorType,
                errorTitle,
                errorTraceId,
                userName: credential.userName,
                originalError: axiosError.message
            })

            const errorToThrow = new Error(errorMessage) as Error & {
                statusCode?: number
                code?: string
                type?: string
                title?: string
                traceId?: string
                detail?: string
                response?: unknown
            }

            errorToThrow.statusCode = statusCode
            errorToThrow.response = error

            // Set error properties if they exist
            if (errorCode) errorToThrow.code = errorCode
            if (errorType) errorToThrow.type = errorType
            if (errorTitle) errorToThrow.title = errorTitle
            if (errorTraceId) errorToThrow.traceId = errorTraceId
            errorToThrow.detail = errorMessage

            throw errorToThrow
        }

        const errorMessage =
            error instanceof Error
                ? error.message
                : 'An unknown error occurred during sign in'

        logger.error('Sign-in unknown error:', {
            errorMessage,
            errorType: typeof error,
            userName: credential.userName,
            stack: error instanceof Error ? error.stack : undefined
        })

        throw new Error(errorMessage)
    }
}

export async function apiSignUp(data: SignUpCredential) {
    return ApiService.fetchData<SignInResponse>({
        url: '/sign-up',
        method: 'post',
        data,
    })
}

export async function apiSignOut() {
    return ApiService.fetchData({
        url: '/api/auth/logout',
        method: 'post',
    })
}

export async function apiForgotPassword(data: ForgotPassword) {
    return ApiService.fetchData({
        url: '/api/auth/request-password-reset',
        method: 'post',
        data,
    })
}

export async function apiResetPassword(data: ResetPassword) {
    return ApiService.fetchData({
        url: '/api/auth/reset-password',
        method: 'post',
        data,
    })
}

export async function apiValidateResetToken(token: string) {
    return ApiService.fetchData({
        url: `/api/auth/validate/${encodeURIComponent(token)}`,
        method: 'get',
    })
}
